import { useContext, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import AuthContext from '../../context/AuthProvider';
import { fetchAdminBundle } from '../../api/translations';

// Loads the authenticated admin translation bundle (admin + shared scopes) and
// merges it into the shared i18next instance AFTER login. Admin-only strings
// are never shipped in the public /locales file, so before this runs the public
// site only has frontend + shared strings. Renders nothing.
//
// Re-runs whenever the logged-in user or the active language changes.
const AdminTranslationsLoader = () => {
    const { i18n } = useTranslation();
    const { auth } = useContext(AuthContext);
    const loadedRef = useRef({}); // { [lang]: true } — avoid redundant reloads

    const isLoggedIn = Boolean(auth?.userInfo);
    const lang = i18n.language;

    useEffect(() => {
        if (!isLoggedIn) {
            loadedRef.current = {};
            return;
        }

        let cancelled = false;
        const base = (lang || 'en').split('-')[0];

        (async () => {
            try {
                const res = await fetchAdminBundle(base);
                const data = res?.data || {};
                if (cancelled || !data || Object.keys(data).length === 0) return;

                // Merge into the default namespace, overwriting only these keys.
                i18n.addResourceBundle(base, 'translation', data, true, true);
                loadedRef.current[base] = true;

                // Nudge react-i18next to re-render with the newly added strings.
                i18n.changeLanguage(i18n.language);
            } catch (err) {
                // Non-fatal: admin screens fall back to key names if this fails.
                console.error('Failed to load admin translation bundle:', err);
            }
        })();

        return () => { cancelled = true; };
    }, [isLoggedIn, lang, i18n]);

    return null;
};

export default AdminTranslationsLoader;
